import { memo, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import Swiper from "react-id-swiper";
import AppGlobalsContext from "app/AppGlobalsContext";
import "./ProductsCarousel.scss";
import ProductCard from "app/pages/.shared/ProductCard/ProductCard";
import IconNextPlain from "app/pages/.shared/static/icons/IconNextPlain";
import IconPrevPlain from "app/pages/.shared/static/icons/IconPrevPlain";

const IMG_SIZES_DESKTOP = {
	large: { width: 330, height: 212 },
	xlarge: { width: 330, height: 212 },
};

const SHOW_SLIDES_NUMBER = {
	[RESOLUTION.XLARGE]: 3,
	[RESOLUTION.LARGE]: 3,
	[RESOLUTION.MEDIUM]: 2.3,
	[RESOLUTION.SMALL]: 1.2,
};

const ProductsCarousel = ({
	products = [],
	hidePriceBanner,
	onItemClick = () => {},
	noSwiping,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const showArrows = !isMobile && products.length > 3;

	const [swiper, setSwiper] = useState(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);

	const updateSwiper = swiper => {
		setSwiper(swiper);
	};

	const slidePrev = () => {
		swiper.slidePrev();
	};

	const slideNext = () => {
		swiper.slideNext();
	};

	const swiperParams = {
		shouldSwiperUpdate: true,
		getSwiper: updateSwiper,
		slidesPerView: SHOW_SLIDES_NUMBER[resolution],
		spaceBetween: 24,
		centeredSlides: isMobile && products.length === 1,
		autoHeight: true,
		noSwiping,
	};

	useEffect(() => {
		if (swiper) {
			swiper.on("slideChangeTransitionEnd", () => {
				const isBeginning = swiper?.isBeginning;
				const isEnd = swiper?.isEnd;

				setIsBeginning(isBeginning);

				setIsEnd(isEnd);
			});
		}

		return () => {
			if (swiper) {
				swiper.off("slideChangeTransitionEnd");
			}
		};
	}, [swiper]);

	return (
		<div className="products-carousel">
			<Swiper {...swiperParams}>
				{products.map(product => {
					return (
						<div
							className="products-carousel__item"
							key={product.uri}
							onClick={onItemClick}
							data-testid={`product-${product?.uri}`}
						>
							<ProductCard
								hidePriceBanner={hidePriceBanner}
								isMobile={isMobile}
								product={product}
								desktopImageSizes={IMG_SIZES_DESKTOP}
								displayCardSliderOnMob={false} // Désactivation temporaire du slider d'images en raison de problèmes de compatibilité et de swipe du carrousel.
							/>
						</div>
					);
				})}
			</Swiper>
			{showArrows && !isEnd && swiper && (
				<div
					className="products-carousel__button products-carousel__button--next"
					onClick={slideNext}
				>
					<IconNextPlain />
				</div>
			)}
			{showArrows && !isBeginning && swiper && (
				<div
					className="products-carousel__button products-carousel__button--previous"
					onClick={slidePrev}
				>
					<IconPrevPlain />
				</div>
			)}
		</div>
	);
};

ProductsCarousel.propTypes = {
	products: PropTypes.arrayOf(PropTypes.object),
	hidePriceBanner: PropTypes.bool,
	onItemClick: PropTypes.func,
	noSwiping: PropTypes.bool,
};

export default memo(ProductsCarousel);
